<template>
  <div class="nup-board">
    <section class="nup-entry">
      <h3 class="nup-board__heading">
        {{ copy.entryTitle }}
        <img
          class="nup-entry__badge"
          :class="isEn ? 'is-en' : ''"
          :src="badgeSrc"
          :alt="copy.guaranteed"
        >
      </h3>
      <p class="nup-board__desc nup-entry__desc">
        <img
          v-if="showEntryCheck"
          class="nup-entry__check"
          :src="checkImg"
          alt=""
        >
        <span>{{ copy.entryDesc }}</span>
      </p>
      <img
        v-if="pickedSrc"
        class="nup-entry__picked"
        :class="isEn ? 'is-en' : ''"
        :src="pickedSrc"
        alt=""
      >
      <div
        v-else
        class="nup-entry__grid"
      >
        <div
          v-for="item in gridItems"
          :key="item.key"
          class="nup-entry__cell"
          :class="[canPick ? 'is-pickable' : '', item.key === highlightKey ? 'is-on' : '']"
          @click="onCellClick(item.key)"
        >
          <img
            class="nup-entry__grid-img"
            :src="item.image"
            alt=""
          >
          <img
            v-if="item.key === highlightKey"
            class="nup-entry__mark"
            :src="selectedMark"
            alt=""
          >
        </div>
      </div>
      <div
        v-if="showChoiceBtn"
        class="nup-entry__pick-wrap"
      >
        <button
          type="button"
          class="nup-entry__pick"
          :style="{ backgroundImage: 'url(' + btnBg + ')' }"
          @click="$emit('pick', highlightKey)"
        >
          {{ copy.chooseThis }}
        </button>
      </div>
    </section>

    <div
      class="nup-board__plus"
      aria-hidden="true"
    >
      <img
        class="nup-board__plus-img"
        :src="plusImg"
        alt=""
      >
    </div>

    <section class="nup-adv">
      <h3 class="nup-board__heading">
        {{ copy.advancedTitle }}
      </h3>
      <p class="nup-board__desc">{{ copy.advancedDesc }}</p>
      <div class="nup-adv__track">
        <div
          v-for="(tier, index) in advancedTiers"
          :key="tier.id"
          class="nup-adv__item"
          :class="itemClass(index)"
        >
          <span class="nup-adv__dot" />
          <RewardTierRow :tier="tier" />
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, ref } from 'vue-demi';
import { DEFAULT_CHOICE_KEY, isChoiceKey, type ChoiceKey } from '@ark/nup-shared';
import { getCopy } from '../constants/copy';
import { ADVANCED_TIERS, CHOICE_OPTIONS, findChoiceGridImage, findChoiceSelectedImage, findChoiceWideImage, findEntryBadge } from '../constants/rewards';
import { advancedFilledCount, hasEntryAward, progressItemClass } from '../utils/progress-stage';
import { useNupLang } from '../composables/use-nup-lang';
import { usePageTypeFlag } from '../composables/use-page-type';
import btnBg from '../assets/rewards/btn-bg.png';
import checkImg from '../assets/rewards/entry-check.png';
import plusImg from '../assets/rewards/plus.png';
import RewardTierRow from './reward-tier-row.vue';
const __sfc_main = {};
__sfc_main.props = {
  showChoiceBtn: {
    type: Boolean,
    default: false
  },
  choiceKey: {
    type: String,
    default: ''
  },
  enrolled: {
    type: Boolean,
    default: false
  },
  netDepositAmount: {
    type: Number,
    default: null
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const advancedTiers = ADVANCED_TIERS;
  const {
    isPC
  } = usePageTypeFlag();
  const {
    isEn
  } = useNupLang();
  const copy = computed(() => getCopy(isEn.value));
  const badgeSrc = computed(() => findEntryBadge(isEn.value));
  const draftKey = ref<ChoiceKey>(DEFAULT_CHOICE_KEY);
  const committedKey = computed<ChoiceKey | null>(() => {
    return isChoiceKey(props.choiceKey) ? props.choiceKey : null;
  });
  const canPick = computed(() => props.showChoiceBtn);
  const highlightKey = computed<ChoiceKey | null>(() => {
    if (committedKey.value) {
      return committedKey.value;
    }
    if (!props.enrolled) {
      return null;
    }
    return draftKey.value;
  });
  const pickedSrc = computed(() => {
    return committedKey.value ? findChoiceWideImage(committedKey.value, isEn.value, isPC.value) : '';
  });
  const selectedMark = computed(() => {
    return findChoiceSelectedImage(isPC.value);
  });
  function onCellClick(key: ChoiceKey): void {
    if (!canPick.value) {
      return;
    }
    draftKey.value = key;
  }
  const gridItems = computed(() => {
    return CHOICE_OPTIONS.map(item => {
      return {
        key: item.key,
        image: findChoiceGridImage(item.key, isPC.value, isEn.value)
      };
    });
  });
  const filledCount = computed(() => {
    return advancedFilledCount(props.enrolled, props.netDepositAmount);
  });
  const showEntryCheck = computed(() => {
    return hasEntryAward(props.enrolled, props.netDepositAmount);
  });
  function itemClass(index: number): string {
    return progressItemClass(index, showEntryCheck.value, filledCount.value);
  }
  return {
    btnBg,
    checkImg,
    plusImg,
    advancedTiers,
    isEn,
    copy,
    badgeSrc,
    canPick,
    highlightKey,
    pickedSrc,
    selectedMark,
    onCellClick,
    gridItems,
    showEntryCheck,
    itemClass
  };
};
__sfc_main.components = Object.assign({
  RewardTierRow
}, __sfc_main.components);
export default __sfc_main;
</script>

<style scoped>
.nup-board__heading {
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
  color: #000;
}

.nup-board__desc {
  margin: 12px 0 28px;
  font-size: 26px;
  line-height: 1.4;
  color: rgba(48, 48, 48, 0.72);
}

.nup-entry__badge {
  display: block;
  width: 92px;
  height: 53px;
  margin-left: 12px;
  flex-shrink: 0;
}

.nup-entry__badge.is-en {
  width: 190px;
  height: 61px;
}

.nup-entry__desc {
  display: flex;
  align-items: center;
}

.nup-entry__check {
  display: block;
  width: 32px;
  height: 32px;
  margin-right: 8px;
  flex-shrink: 0;
}

.nup-entry__grid {
  display: grid;
  grid-template-columns: 330px 330px;
  justify-content: center;
  gap: 12px;
  overflow: visible;
}

.nup-entry__cell {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: visible;
}

.nup-entry__cell.is-pickable {
  cursor: pointer;
}

.nup-entry__cell.is-on {
  z-index: 3;
}

.nup-entry__grid-img {
  display: block;
  position: relative;
  z-index: 1;
  width: 330px;
  height: 140px;
  max-width: none;
  object-fit: fill;
}

.nup-entry__mark {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 2;
  width: 353px;
  height: 163px;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.nup-entry__picked {
  display: block;
  width: 670px;
  height: 188px;
  max-width: 100%;
  margin: 0 auto;
  object-fit: contain;
}

.nup-entry__picked.is-en {
  width: 670px;
  height: 188px;
}

.nup-entry__pick-wrap {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

.nup-entry__pick {
  display: block;
  width: 614px;
  height: 106px;
  padding: 0;
  border: none;
  background-color: transparent;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  color: #fff;
  font-size: 40px;
  font-weight: 600;
  line-height: 106px;
  text-align: center;
  cursor: pointer;
}

.nup-board__plus {
  display: flex;
  align-items: center;
  width: 100%;
  margin: 40px 0;
}

.nup-board__plus::before,
.nup-board__plus::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e6e6e6;
}

.nup-board__plus-img {
  display: block;
  width: 61px;
  height: 63px;
  margin: 0 16px;
  flex-shrink: 0;
}

.nup-adv__track {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.nup-adv__item {
  position: relative;
  z-index: 1;
}

.nup-adv__item:not(:last-child)::before {
  content: '';
  position: absolute;
  z-index: 0;
  left: 13px;
  top: 50%;
  width: 4px;
  height: calc(100% + 32px);
  background: #e6e6e6;
}

.nup-adv__item.is-linked:not(:last-child)::before {
  background: #ff6900;
}

.nup-adv__dot {
  position: absolute;
  left: 0;
  top: 50%;
  z-index: 2;
  width: 30px;
  height: 30px;
  margin-top: -15px;
  border: 6px solid #d8d8d8;
  border-radius: 50%;
  background: #fff;
  box-sizing: border-box;
}

.nup-adv__item.is-active .nup-adv__dot {
  border-color: #ff6900;
  background: #fff;
}

.nup-adv__item.is-on .nup-adv__dot {
  border-color: #ff6900;
  background: #ff6900;
}

.nup-adv__item.is-on .nup-adv__dot::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 0;
  width: 7px;
  height: 12px;
  border-right: 3px solid #fff;
  border-bottom: 3px solid #fff;
  transform: rotate(45deg);
}

.nup-page.is-h5.is-en .nup-board__heading {
  color: #000;
  font-family: 'BR Hendrix', Arial, sans-serif;
  font-size: 42px;
  font-style: normal;
  font-weight: 600;
}

.nup-page.is-h5.is-en .nup-board__desc {
  color: #000;
  font-family: 'BR Hendrix', Arial, sans-serif;
  font-size: 28px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  opacity: 0.8;
  white-space: pre-line;
}

.nup-page.is-pc .nup-board__heading {
  justify-content: center;
  font-size: 42px;
}

.nup-page.is-pc .nup-board__desc {
  margin: 8px 0 24px;
  font-size: 24px;
  text-align: center;
}

.nup-page.is-pc.is-en .nup-board__desc {
  white-space: pre-line;
}

.nup-page.is-pc .nup-entry__desc {
  justify-content: center;
}

.nup-page.is-pc .nup-entry__badge {
  width: 46px;
  height: 26px;
  margin-left: 8px;
}

.nup-page.is-pc .nup-entry__badge.is-en {
  width: 95px;
  height: 31px;
}

.nup-page.is-pc .nup-entry__check {
  width: 18px;
  height: 18px;
  margin-right: 6px;
}

.nup-page.is-pc .nup-entry__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  width: 1200px;
  max-width: 100%;
  margin: 0 auto;
  gap: 24px;
}

.nup-page.is-pc .nup-entry__grid-img {
  width: 100%;
  height: auto;
}

.nup-page.is-pc .nup-entry__mark {
  width: 306px;
  height: 163px;
}

.nup-page.is-pc .nup-entry__picked,
.nup-page.is-pc .nup-entry__picked.is-en {
  width: 591px;
  height: 152px;
}

.nup-page.is-pc .nup-entry__pick {
  width: 360px;
  height: 60px;
  font-size: 20px;
  line-height: 60px;
}

.nup-page.is-pc .nup-board__plus {
  width: 1200px;
  max-width: 100%;
  margin: 40px auto;
}

.nup-page.is-pc .nup-board__plus-img {
  width: 36px;
  height: 37px;
  margin: 0 12px;
}

.nup-page.is-pc .nup-adv__track {
  width: 1200px;
  max-width: 100%;
  margin: 0 auto;
  gap: 20px;
}

.nup-page.is-pc .nup-adv__item:not(:last-child)::before {
  left: 10px;
  width: 2px;
  height: calc(100% + 20px);
}

.nup-page.is-pc .nup-adv__dot {
  width: 22px;
  height: 22px;
  margin-top: -11px;
  border-width: 4px;
}

.nup-page.is-pc .nup-adv__item.is-on .nup-adv__dot::after {
  left: 4px;
  top: 0px;
  width: 5px;
  height: 9px;
  border-right-width: 2px;
  border-bottom-width: 2px;
}
</style>
